// The FAQ hub's progressive-enhancement layer: category pills, the
// sub-category sidebar, and the ?category= / ?subcategory= URL parameters.
//
// It FILTERS server-rendered nodes. It never creates one. That sentence is the
// whole contract, and it is the one thing this file's predecessor got wrong:
// the section used to ship its 241 answers as a `const ALL_RAW = [...]` array
// inside a <script> and build every question, answer and heading with
// document.createElement, so a crawler that ran no JavaScript received three
// empty <div>s from the most citation-relevant page Fireclay owns. See
// docs/adr/adr-20260901-02-faq-is-article-backed-and-server-rendered.md, and
// theme/tests/faq-listings.dom.test.js, whose last assertion is that the answer
// nodes on the page are the same ones before and after every interaction here.
//
// Everything below is therefore optional. With this file blocked, unparsed or
// simply slow, /pages/frequently-asked-questions still serves every question
// and every answer as visible text inside <details> elements, keyboard
// operable, with the sub-category sidebar working as in-page anchors.
//
// The pills are the one exception, and are hidden until this runs: a filter
// control that cannot filter is worse than no control. They are <button>s
// rather than links for a reason worth keeping — `?category=` links would give
// a crawler eight more URLs serving the same 241 answers, off the page whose
// citability this phase exists to protect.

const DEFAULT_SIDEBAR_TITLE = 'Sub-categories';

/**
 * The filter a URL asks for. Absent or empty parameters read as null, so
 * `?category=` is "no category" rather than a category named "".
 */
export function parseFilterFromSearch(search) {
    const params = new URLSearchParams(search);
    const clean = (value) => (value && value.trim() ? value.trim() : null);
    return {
        category: clean(params.get('category')),
        subcategory: clean(params.get('subcategory')),
    };
}

/** Whether a node tagged with `data-faq-category` / `-subcategory` survives a filter. */
export function matchesFilter(dataset, { category, subcategory }) {
    if (category && dataset.faqCategory !== category) return false;
    if (subcategory && dataset.faqSubcategory !== subcategory) return false;
    return true;
}

class FaqListing extends HTMLElement {
    connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.pills = [...this.querySelectorAll('[data-faq-category].faq-listing-pill')];
        this.pillBar = this.querySelector('[data-faq-pills]');
        this.sidebarItems = [...this.querySelectorAll('[data-faq-sidebar-item]')];
        this.sidebarTitle = this.querySelector('[data-faq-sidebar-title]');
        this.groups = [...this.querySelectorAll('[data-faq-group]')];
        this.empty = this.querySelector('[data-faq-empty]');

        this.defaultSidebarTitle = this.sidebarTitle?.textContent.trim() || DEFAULT_SIDEBAR_TITLE;

        for (const button of this.pills) {
            button.addEventListener('click', () => {
                // A new category discards the sub-category under the old one —
                // the two axes are nested, not independent.
                this.apply({ category: button.dataset.faqCategory || null, subcategory: null });
            });
        }

        for (const item of this.sidebarItems) {
            const link = item.querySelector('a');
            if (!link) continue;
            link.addEventListener('click', (event) => {
                // The href is a real in-page anchor and stays one, so the
                // sidebar is working navigation before this listener exists.
                // Once it does, filtering is the better answer than scrolling.
                event.preventDefault();
                this.apply({
                    category: item.dataset.faqCategory || null,
                    subcategory: item.dataset.faqSubcategory || null,
                });
            });
        }

        // Only now, because only now do they do anything.
        if (this.pillBar) this.pillBar.hidden = false;

        this.apply(parseFilterFromSearch(window.location.search), { pushUrl: false });
    }

    apply(filter, { pushUrl = true } = {}) {
        const { category, subcategory } = filter;
        let visible = 0;

        for (const group of this.groups) {
            const show = matchesFilter(group.dataset, filter);
            group.hidden = !show;
            if (show) visible += 1;
        }

        // The sidebar narrows to the active category but ignores the active
        // sub-category: hiding the siblings you would switch to is how a filter
        // becomes a trap.
        for (const item of this.sidebarItems) {
            item.hidden = !matchesFilter(item.dataset, { category, subcategory: null });
            const link = item.querySelector('a');
            if (link) {
                link.classList.toggle(
                    'is-active',
                    Boolean(subcategory) && item.dataset.faqSubcategory === subcategory,
                );
            }
        }

        for (const button of this.pills) {
            button.classList.toggle(
                'is-active',
                (button.dataset.faqCategory || null) === category,
            );
        }

        if (this.sidebarTitle) {
            this.sidebarTitle.textContent = category || this.defaultSidebarTitle;
        }
        if (this.empty) this.empty.hidden = visible > 0;

        if (pushUrl) this.writeUrl(filter);
    }

    // replaceState, not pushState: the pills are a view control, and filling
    // the back button with filter states would bury the page the reader
    // arrived from.
    //
    // The filter owns two query keys and nothing else on the URL, so this edits
    // the existing query string rather than composing a new one. A reader can
    // arrive here on a campaign link — snippets/attribution-capture.liquid reads
    // gclid, gbraid, wbraid and the utm_* set off window.location.search — and
    // rebuilding from empty would drop all of them the first time anyone touched
    // a pill. Capture runs at page load, so the stored first-touch survives, but
    // the URL the reader then copies or shares would not. The fragment is kept
    // for the same reason: it is someone else's, not the filter's.
    writeUrl({ category, subcategory }) {
        const params = new URLSearchParams(window.location.search);
        params.delete('category');
        params.delete('subcategory');
        if (category) params.set('category', category);
        if (subcategory) params.set('subcategory', subcategory);

        const query = params.toString();
        window.history.replaceState(
            {},
            '',
            `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
        );
    }
}

if (!customElements.get('faq-listing')) {
    customElements.define('faq-listing', FaqListing);
}
